import React, { useCallback, useEffect, useState } from "react";
import "./InfoStep.scss";
import {
  Checkbox,
  Col,
  Input,
  InputProps,
  Radio,
  Row,
  Select,
  SelectProps,
  Spin,
  Switch,
} from "antd";
import ProfileMap from "@components/ProfileMap/ProfileMap";
import { InfoCircleOutlined, UndoOutlined } from "@ant-design/icons";
import { IProductForm, IUserInfo } from "@utils/Interfaces";
import HayCalculations from "@utils/HayCalculations";
import Validates from "@utils/Validates";
import Helpers from "@utils/Helpers";
import { useUnit } from "contexts/UnitContext";
import { useUser } from "contexts/UserContext";
const { Option } = Select;

const inputProps: InputProps & SelectProps = {
  size: "large",
  variant: "filled",
};

interface IInfoStep {
  productForm: IProductForm;
  setProductForm: (value: any) => void;
  userProfile: IUserInfo;
  isSmartEstimating: boolean;
  isSmartEstimate: boolean;
  dimensions: any;
  hayTypeClassify: string;
  setHayTypeClassify: (type: string) => void;
  isEstimate: boolean;
  setIsEstimate: (is: boolean) => void;
  isTypingTotalWeight: boolean;
  setIsTypingTotalWeight: (is: boolean) => void;
  sizeSelect: string;
  selectedWeightUnit: any;
  handleChangeUnit: (key: string, unit: string) => void;
  handleUndoAll: () => void;
}

const InfoStep: React.FC<IInfoStep> = ({
  productForm,
  setProductForm,
  userProfile,
  isSmartEstimating,
  isSmartEstimate,
  dimensions,
  setHayTypeClassify,
  isEstimate,
  setIsEstimate,
  setIsTypingTotalWeight,
  sizeSelect,
  selectedWeightUnit,
  handleChangeUnit,
  handleUndoAll,
}) => {
  const { units, convertWeight } = useUnit();
  const { userAddresses, isDeveloperMode } = useUser();
  const [selectedLocation, setSelectedLocation] = useState<any>({});
  const [selectedPostion, setSelectedPostion] = useState<any[]>();
  const [selectedAddressId, setSelectedAddressId] = useState<any>(null);
  const [isProteinVerified, setIsProteinVerified] = useState(false);
  useEffect(() => {
    setSelectedAddressId(userProfile.address_id);
  }, []);

  // HANDLE MAPPING LOCATION
  useEffect(() => {
    if (Object.keys(selectedLocation).length > 0) {
      setProductForm({
        ...productForm,
        address: {
          zip_code: selectedLocation.postCode,
          street: selectedLocation.address.road
            ? selectedLocation.address.road
            : selectedLocation.display_name,
          city: selectedLocation.address.city
            ? selectedLocation.address.city
            : selectedLocation.address.town
            ? selectedLocation.address.town
            : selectedLocation.address.county
            ? selectedLocation.address.county
            : selectedLocation.address.village,
          province: selectedLocation.address.province
            ? selectedLocation.address.province
            : selectedLocation.address.state
            ? selectedLocation.address.state
            : selectedLocation.address.city,
          lat: selectedLocation.lat,
          lon: selectedLocation.lon,
        },
      });
    }
  }, [selectedLocation]);

  useEffect(() => {
    if (userAddresses && selectedAddressId) {
      const selected = userAddresses.find(
        (item: any) => item.address_id === selectedAddressId
      );
      const positionGr: any = [];
      userAddresses.map((item: any) => {
        positionGr.push([item.lat || 0, item.lon || 0]);
      });
      setSelectedPostion(positionGr);
      if (selected) {
        const address = {
          street: selected?.street,
          lat: selected?.lat,
          lon: selected?.lon,
          city: selected?.city,
          province: selected?.province,
          zip_code: selected?.postal_code,
        };
        setProductForm((prev: any) => ({
          ...prev,
          address: address,
        }));
      }
    }
  }, [userAddresses, selectedAddressId]);

  const handleChangeAddress = (value: number) => {
    const selected = userAddresses.find(
      (item: any) => item?.address_id === value
    );
    if (selected) {
      const newSelectedPosition = Helpers.moveValueToFirst(selectedPostion, [
        selected?.lat,
        selected?.lon,
      ]);
      setSelectedPostion(newSelectedPosition);
      setSelectedAddressId(value);
      setProductForm({
        ...productForm,
        address: {
          street: selected?.street,
          city: selected?.city,
          province: selected?.province,
          zip_code: selected?.postal_code,
          lat: selected?.lat,
          lon: selected?.lon,
        },
      });
    }
  };

  // CLASSIFY HAY TYPE
  useEffect(() => {
    if (productForm.hay_type !== "") {
      if (productForm.product_category_id === 1) {
        if (
          productForm.hay_type === "Alfalfa" ||
          productForm.hay_type === "Alfalfa mix"
        ) {
          setHayTypeClassify("alfalfa_hay");
        } else {
          setHayTypeClassify("grass_hay");
        }
      } else {
        setHayTypeClassify("straw");
      }
    }
  }, [productForm.hay_type]);

  const handleUpdateProductForm = (
    key: string,
    value: any,
    subKey?: string
  ) => {
    if (
      key === "hay_availability" ||
      key === "moisture" ||
      key === "hay_density" ||
      key === "weight_per_bale" ||
      key === "total_weight"
    ) {
      value = Validates.validateNumberForm(value);
    }

    if (key === "product_category_id") {
      setProductForm({
        ...productForm,
        hay_type: "",
      });
    }

    if (key === "moisture" && value > 100) {
      value = 100;
    }

    if (key === "hay_density" && value !== "") {
      const newBaleWeight = HayCalculations.reEstimateBaleWeight(
        value,
        productForm.shape,
        dimensions,
        productForm.moisture
      );
      const newTotalWeight =
        newBaleWeight * parseFloat(productForm.hay_availability);
      setProductForm({
        ...productForm,
        weight_per_bale:
          selectedWeightUnit.weightPerBale === "t"
            ? newBaleWeight.toFixed(4)
            : newBaleWeight.toFixed(0),
        hay_density: value,
        total_weight:
          selectedWeightUnit.totalWeight === "t"
            ? newTotalWeight.toFixed(4)
            : newTotalWeight.toFixed(0),
      });

      setIsEstimate(false);
      return;
    }

    if (key === "weight_per_bale" && value !== "") {
      const baleWeightInTon = convertWeight(
        value,
        selectedWeightUnit.weightPerBale,
        "t"
      );
      const newDensity = HayCalculations.reEstimateDensity(
        productForm.shape,
        dimensions,
        (parseFloat(baleWeightInTon) * 1000).toString()
      );

      const newTotalWeight =
        baleWeightInTon * parseFloat(productForm.hay_availability);

      setProductForm({
        ...productForm,
        weight_per_bale: value,
        hay_density: newDensity.toFixed(2),
        total_weight: convertWeight(
          newTotalWeight,
          "t",
          selectedWeightUnit.totalWeight
        ),
      });
      setIsEstimate(false);
      return;
    }

    if (key === "total_weight" && value !== "") {
      setIsTypingTotalWeight(true);

      const totalWeightInTon = convertWeight(
        value,
        selectedWeightUnit.totalWeight,
        "t"
      );
      // unit: ton
      const newBaleWeight =
        totalWeightInTon / parseFloat(productForm.hay_availability);

      // unit: kg/ft3
      const newDensity = HayCalculations.reEstimateDensity(
        productForm.shape,
        dimensions,
        (newBaleWeight * 1000).toString()
      );

      setProductForm({
        ...productForm,
        hay_density: newDensity.toFixed(2),
        total_weight: value,
        weight_per_bale: convertWeight(
          newBaleWeight,
          "t",
          selectedWeightUnit.weightPerBale
        ),
      });

      setIsEstimate(false);
      setTimeout(() => {
        setIsTypingTotalWeight(false);
      }, 800);
      return;
    }

    if (key === "product_category_id") {
      setProductForm({
        ...productForm,
        product_category_id: value,
        hay_type: "",
      });
      return;
    }

    if (subKey) {
      setProductForm({
        ...productForm,
        address: { ...productForm.address, [subKey]: value },
      });
    } else {
      setProductForm({ ...productForm, [key]: value });
    }
  };

  return (
    <div className="InfoStep">
      <div className="forms-group">
        <div className="forms-title">Location</div>
        <Row gutter={16}>
          <Col span={12}>
            <ProfileMap
              position={selectedPostion}
              setPosition={setSelectedPostion}
              onSelected={setSelectedLocation}
            />
          </Col>
          <Col span={12}>
            <div className="form-group">
              <Select
                size="large"
                value={selectedAddressId}
                onChange={handleChangeAddress}
              >
                {userAddresses.map((item: any, index: number) => (
                  <Option key={index} value={item.address_id}>
                    {item.label}
                  </Option>
                ))}
              </Select>
            </div>
            <div className="form-group map-search-input">
              <div className="form-title">Street Address</div>
              <Input
                {...inputProps}
                disabled
                value={productForm.address.street}
                onChange={(e) => {
                  const query = e.target.value;
                  handleUpdateProductForm("address", query, "street");
                }}
              />
            </div>
            <div className="form-group">
              <div className="form-title">City/Town/County</div>
              <Input
                {...inputProps}
                disabled
                value={productForm.address.city}
                onChange={(e) =>
                  handleUpdateProductForm("address", e.target.value, "city")
                }
              />
            </div>
            <div className="form-group">
              <div className="form-title">Province</div>
              <Input
                {...inputProps}
                disabled
                value={productForm.address.province}
                onChange={(e) =>
                  handleUpdateProductForm("address", e.target.value, "province")
                }
              />
            </div>
            <div className="form-group">
              <div className="form-title">Postal/Zip Code</div>
              <Input
                {...inputProps}
                disabled
                value={productForm.address.zip_code}
                onChange={(e) =>
                  handleUpdateProductForm("address", e.target.value, "zip_code")
                }
              />
            </div>
          </Col>
        </Row>
      </div>
      <div className="forms-group">
        <div className="forms-title">
          Product Information
          {isSmartEstimating && (
            <div className="estimate-wrap small">
              <Spin className="me-2" />
              <span>Estimating.....</span>
            </div>
          )}
        </div>
        <div className="mb-3 protein-checkbox-group">
          <div className="label mb-2">
            Do you have a recent hay analysis from a certified lab?
          </div>
          <Radio.Group
            value={productForm.crude_protein}
            onChange={(value) => {
              handleUpdateProductForm("crude_protein", value);
            }}
            options={[
              { label: "Yes", value: 1 },
              { label: "No", value: 0 },
            ]}
          />
        </div>
        <Row gutter={[16, 15]}>
          <Col span={12}>
            <div className="form-group">
              <div className="form-title wrap">
                Product Category<span className="text-danger">*</span>
                {isSmartEstimate && !isSmartEstimating && (
                  <span className="ms-auto text-muted small">
                    H2U proposed value
                  </span>
                )}
              </div>
              <Select
                {...inputProps}
                value={productForm.product_category_id}
                onChange={(value) => {
                  handleUpdateProductForm("product_category_id", value);
                }}
              >
                <Option value={1}>Hay</Option>
                <Option value={2}>Straw</Option>
                <Option value={3} disabled>
                  <span
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    Hay Seed
                    <span>Coming Soon</span>
                  </span>
                </Option>
              </Select>
            </div>
          </Col>
          <Col span={12}>
            <div className="form-group">
              <div className="form-title wrap">
                Product Type<span className="text-danger">*</span>
                {isSmartEstimate && !isSmartEstimating && (
                  <span className="ms-auto text-muted small">
                    H2U proposed value
                  </span>
                )}
              </div>
              {productForm.product_category_id === 1 ? (
                <Select
                  {...inputProps}
                  value={productForm.hay_type}
                  onChange={(value) =>
                    handleUpdateProductForm("hay_type", value)
                  }
                >
                  <Option value="Alfalfa">Alfalfa</Option>
                  <Option value="Alfalfa mix">Alfalfa Mix</Option>
                  <Option value="Brome Grass">Brome Grass</Option>
                  <Option value="Timothy">Timothy</Option>
                  <Option value="Grass mix">Grass Mix</Option>
                  <Option value="Orchard Grass">Orchard Grass</Option>
                  <Option value="Clover">Clover</Option>
                  <Option value="Sainfoin">Sainfoin</Option>
                  <Option value="Others">Others</Option>
                </Select>
              ) : (
                <Select
                  {...inputProps}
                  value={productForm.hay_type}
                  onChange={(value) =>
                    handleUpdateProductForm("hay_type", value)
                  }
                >
                  <Option value="Wheat Straw">Wheat Straw</Option>
                  <Option value="Barley Straw">Barley Straw </Option>
                  <Option value="Oat Straw">Oat Straw</Option>
                  <Option value="Rye Straw">Rye Straw</Option>
                  <Option value="Canola Straw">Canola Straw</Option>
                  <Option value="Flax Straw">Flax Straw</Option>
                  <Option value="Others">Others</Option>
                </Select>
              )}
            </div>
          </Col>
          <Col span={12}>
            <div className="form-group">
              <div className="form-title">
                Availability<span className="text-danger">*</span>
              </div>
              <Input
                {...inputProps}
                addonAfter={"Bales"}
                value={Helpers.formatNumberInput(productForm.hay_availability)}
                onChange={(e) => {
                  if (e.target.value.includes(".")) {
                    e.target.value = e.target.value.slice(0, -1);
                  }
                  handleUpdateProductForm("hay_availability", e.target.value);
                }}
              />
            </div>
          </Col>
          <Col span={12}>
            <div className="form-group">
              <div className="form-title wrap">
                Shape<span className="text-danger">*</span>
                {isSmartEstimate && !isSmartEstimating && (
                  <span className="ms-auto text-muted small">
                    H2U proposed value
                  </span>
                )}
              </div>
              <Select
                {...inputProps}
                value={productForm.shape}
                onChange={(value) => {
                  handleUpdateProductForm("shape", value);
                }}
              >
                <Option value="ssquare">Small Square</Option>
                <Option value="lsquare">Large Square</Option>
                <Option value="round">Round</Option>
              </Select>
            </div>
          </Col>
          <Col span={12} className={`${!isDeveloperMode && "d-none"}`}>
            <div className="form-group">
              <div className="form-title">
                Dimesions ({units.length})<span className="text-danger">*</span>
              </div>
              <Row>
                <Col span={0}>
                  <Select
                    {...inputProps}
                    value={sizeSelect}
                    className="size-select"
                    disabled={productForm.shape === "round"}
                  >
                    <Option value="small">Small</Option>
                    <Option value="large">Large</Option>
                  </Select>
                </Col>
                {productForm.shape === "ssquare" ||
                productForm.shape === "lsquare" ? (
                  <Col span={24}>
                    <Input
                      {...inputProps}
                      addonBefore={
                        <Input
                          value={dimensions.width === 0 ? "" : dimensions.width}
                          variant="borderless"
                          prefix={"W"}
                          suffix={units.length}
                        />
                      }
                      addonAfter={
                        <Input
                          value={
                            dimensions.length === 0 ? "" : dimensions.length
                          }
                          variant="borderless"
                          prefix={"L"}
                          suffix={units.length}
                        />
                      }
                      className="size-input-square"
                      prefix={"H"}
                      suffix={units.height}
                      value={dimensions.height === 0 ? "" : dimensions.height}
                    />
                  </Col>
                ) : (
                  <Col span={24}>
                    <Input
                      {...inputProps}
                      addonAfter={
                        <Input
                          value={
                            dimensions.diameter === 0 ? "" : dimensions.diameter
                          }
                          variant="borderless"
                          prefix={"D"}
                          suffix={units.length}
                        />
                      }
                      className="size-input-shape"
                      value={dimensions.width === 0 ? "" : dimensions.width}
                      prefix={"W"}
                      suffix={units.length}
                    />
                  </Col>
                )}
              </Row>
            </div>
          </Col>
          <Col span={12}>
            <div className="form-group">
              <div className="form-title">
                Rain<span className="text-danger">*</span>
              </div>
              <Select
                {...inputProps}
                value={productForm.rain}
                onChange={(value) => handleUpdateProductForm("rain", value)}
              >
                <Option value="Yes">Yes</Option>
                <Option value="No">No</Option>
              </Select>
            </div>
          </Col>
          <Col span={12}>
            <div className="form-group">
              <div className="form-title">
                Moisture (%)<span className="text-danger">*</span>
              </div>
              <Input
                {...inputProps}
                value={productForm.moisture}
                onChange={(e) =>
                  handleUpdateProductForm("moisture", e.target.value)
                }
                suffix={"%"}
              />
            </div>
          </Col>
          <Col span={12}>
            <div className="form-group">
              <div className="form-title wrap">
                Compression (Baling)
                {isSmartEstimate && !isSmartEstimating && (
                  <span className="ms-auto text-muted small">
                    H2U proposed value
                  </span>
                )}
              </div>
              <Select
                {...inputProps}
                value={productForm.hay_compression}
                onChange={(value) =>
                  handleUpdateProductForm("hay_compression", value)
                }
              >
                <Option value="loose">Loose</Option>
                <Option value="medium">Medium</Option>
                <Option value="high">High</Option>
              </Select>
            </div>
          </Col>
          <Col span={12} className={`${!isDeveloperMode && "d-none"}`}>
            <div className="form-group">
              <div className="form-title wrap">
                Density <InfoCircleOutlined className="info-btn" />
                {isSmartEstimate && !isSmartEstimating && (
                  <span className="ms-auto text-muted small">
                    H2U proposed value
                  </span>
                )}
                <span
                  className={`sub-label ${!isEstimate ? "customized" : ""}`}
                >
                  {isEstimate ? "Estimated" : "Customized"}
                </span>
              </div>
              <Input
                value={Helpers.formatNumberInput(productForm.hay_density)}
                onChange={(e) =>
                  handleUpdateProductForm("hay_density", e.target.value)
                }
                {...inputProps}
                className={`${!isEstimate ? "customized" : ""}`}
                addonAfter={
                  <>
                    <span className="unit">kg/ft³</span>
                    <button
                      className="undo-btn"
                      disabled={isEstimate}
                      onClick={handleUndoAll}
                    >
                      <UndoOutlined />
                    </button>
                  </>
                }
              />
            </div>
          </Col>
          <Col span={12}>
            <div className="form-group">
              <div className="form-title wrap">
                Weight/Bale <InfoCircleOutlined className="info-btn" />
                <span
                  className={`sub-label ${!isEstimate ? "customized" : ""}`}
                >
                  {isEstimate ? "Estimated" : "Customized"}
                </span>
              </div>
              <Input
                {...inputProps}
                className={`${!isEstimate ? "customized" : ""}`}
                value={Helpers.formatNumberInput(productForm.weight_per_bale)}
                onChange={(e) => {
                  handleUpdateProductForm("weight_per_bale", e.target.value);
                }}
                addonAfter={
                  <>
                    <Select
                      className="weight-select"
                      value={selectedWeightUnit.weightPerBale}
                      onChange={(value) =>
                        handleChangeUnit("weightPerBale", value)
                      }
                    >
                      <Option value="lb">lb</Option>
                      <Option value="kg">kg</Option>
                      <Option value="t">ton</Option>
                    </Select>
                    <button
                      className="undo-btn"
                      disabled={isEstimate}
                      onClick={handleUndoAll}
                    >
                      <UndoOutlined />
                    </button>
                  </>
                }
              />
            </div>
          </Col>
          <Col span={12}>
            <div className="form-group">
              <div className="form-title wrap">
                Total Weight <InfoCircleOutlined className="info-btn" />
                <span
                  className={`sub-label ${!isEstimate ? "customized" : ""}`}
                >
                  {isEstimate ? "Estimated" : "Customized"}
                </span>
              </div>
              <Input
                value={Helpers.formatNumberInput(productForm.total_weight)}
                {...inputProps}
                onChange={(e: any) =>
                  handleUpdateProductForm("total_weight", e.target.value)
                }
                className={`${!isEstimate ? "customized" : ""}`}
                addonAfter={
                  <>
                    <Select
                      className="weight-select"
                      value={selectedWeightUnit.totalWeight}
                      onChange={(value) =>
                        handleChangeUnit("totalWeight", value)
                      }
                    >
                      <Option value="lb">lb</Option>
                      <Option value="kg">kg</Option>
                      <Option value="t">ton</Option>
                    </Select>
                    <button
                      className="undo-btn"
                      disabled={isEstimate}
                      onClick={handleUndoAll}
                    >
                      <UndoOutlined />
                    </button>
                  </>
                }
              />
            </div>
          </Col>
          <Col span={12}>
            <div className="form-group">
              <div className="form-title">
                Cut<span className="text-danger">*</span>
              </div>
              <Select
                {...inputProps}
                value={productForm.hay_cut}
                onChange={(value) => handleUpdateProductForm("hay_cut", value)}
              >
                <Option value="1st cut">1st Cut</Option>
                <Option value="2nd cut">2nd Cut</Option>
                <Option value="3rd cut">3rd Cut</Option>
              </Select>
            </div>
          </Col>
          <Col span={12}>
            <div className="form-group">
              <div className="form-title">
                Type of Binding<span className="text-danger">*</span>
              </div>
              {productForm.product_category_id === 1 ? (
                <Select
                  {...inputProps}
                  value={productForm.type_of_binding}
                  onChange={(value) =>
                    handleUpdateProductForm("type_of_binding", value)
                  }
                >
                  <Option value="Twine">Twine</Option>
                  <Option value="Wrap">Wrap</Option>
                  <Option value="Net">Net</Option>
                </Select>
              ) : (
                <Select
                  {...inputProps}
                  value={productForm.type_of_binding}
                  onChange={(value) =>
                    handleUpdateProductForm("type_of_binding", value)
                  }
                >
                  <Option value="Twine">Twine</Option>
                  <Option value="Net Wrap">Net Wrap</Option>
                  <Option value="Plastic Wrap">Plastic Wrap</Option>
                </Select>
              )}
            </div>
          </Col>
          <Col span={12}>
            <div className="form-group">
              <div className="form-title">
                Crop<span className="text-danger">*</span>
              </div>
              <Select
                {...inputProps}
                value={productForm.crop}
                onChange={(value) => handleUpdateProductForm("crop", value)}
              >
                <Option value="Old">Old</Option>
                <Option value="New">New</Option>
              </Select>
            </div>
          </Col>
          <Col span={12}>
            <div className="form-group">
              <div className="form-title">
                Storage<span className="text-danger">*</span>
              </div>
              {productForm.product_category_id === 1 ? (
                <Select
                  {...inputProps}
                  value={productForm.storage}
                  onChange={(value) =>
                    handleUpdateProductForm("storage", value)
                  }
                >
                  <Option value="Covered">Covered</Option>
                  <Option value="Open">Open</Option>
                </Select>
              ) : (
                <Select
                  {...inputProps}
                  value={productForm.storage}
                  onChange={(value) =>
                    handleUpdateProductForm("storage", value)
                  }
                >
                  <Option value="Covered">Covered</Option>
                  <Option value="Uncovered">Uncovered</Option>
                  <Option value="Indoors">Indoors</Option>
                </Select>
              )}
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default InfoStep;
